import { useCallback, useEffect, useRef, useState } from 'react';
import type { TrackPoint } from '../api/types';

export interface Pos { lat: number; lon: number; hAF: number; gs: number; track: number; vs: number; phase: TrackPoint['phase']; circuitSeq: number | null; ts: number }

/** Linear interpolation of the track at absolute time ts (clamped to the track's span). */
export function positionAt(pts: TrackPoint[], ts: number): Pos | null {
  if (!pts.length) return null;
  if (ts <= pts[0]!.ts) return toPos(pts[0]!);
  if (ts >= pts[pts.length - 1]!.ts) return toPos(pts[pts.length - 1]!);
  let lo = 0, hi = pts.length - 1;
  while (hi - lo > 1) { const mid = (lo + hi) >> 1; if (pts[mid]!.ts <= ts) lo = mid; else hi = mid; }
  const a = pts[lo]!, b = pts[hi]!, f = (ts - a.ts) / (b.ts - a.ts);
  const lerp = (x: number | null | undefined, y: number | null | undefined) => (x ?? y ?? 0) + ((y ?? x ?? 0) - (x ?? y ?? 0)) * f;
  const track = lerpAngle(a.track ?? 0, b.track ?? 0, f);
  return { lat: lerp(a.lat, b.lat), lon: lerp(a.lon, b.lon), hAF: lerp(a.hAF, b.hAF), gs: lerp(a.gs, b.gs), track, vs: lerp(a.vs, b.vs), phase: f < 0.5 ? a.phase : b.phase, circuitSeq: f < 0.5 ? a.circuitSeq : b.circuitSeq, ts };
}
const toPos = (p: TrackPoint): Pos => ({ lat: p.lat, lon: p.lon, hAF: p.hAF, gs: p.gs ?? 0, track: p.track ?? 0, vs: p.vs ?? 0, phase: p.phase, circuitSeq: p.circuitSeq, ts: p.ts });
function lerpAngle(a: number, b: number, f: number) { let d = ((b - a + 540) % 360) - 180; return (a + d * f + 360) % 360; }

/** Point at fraction f (0..1) of the cumulative length of a [lon, lat, alt] polyline. */
export function fractionAlongPath(path: [number, number, number][], f: number): [number, number, number] {
  if (path.length === 1) return path[0]!;
  const cum = [0]; for (let i = 1; i < path.length; i++) cum.push(cum[i - 1]! + Math.hypot(path[i]![0] - path[i - 1]![0], path[i]![1] - path[i - 1]![1]));
  const target = Math.max(0, Math.min(1, f)) * cum[cum.length - 1]!;
  let i = 1; while (i < cum.length - 1 && cum[i]! < target) i++;
  const seg = cum[i]! - cum[i - 1]!, g = seg > 0 ? (target - cum[i - 1]!) / seg : 0;
  const a = path[i - 1]!, b = path[i]!;
  return [a[0] + (b[0] - a[0]) * g, a[1] + (b[1] - a[1]) * g, a[2] + (b[2] - a[2]) * g];
}

/** Replay clock in seconds relative to startTs. Ticks with requestAnimationFrame-like cadence via setInterval for testability. */
export function useReplayClock({ startTs, endTs, initialT = 0 }: { startTs: number; endTs: number; initialT?: number }) {
  const span = Math.max(0, endTs - startTs);
  const [t, setT] = useState(Math.min(span, Math.max(0, initialT)));
  const [playing, setPlaying] = useState(false);
  const [rate, setRate] = useState(1);
  const last = useRef<number | null>(null);
  useEffect(() => {
    if (!playing) { last.current = null; return; }
    const id = setInterval(() => {
      const now = Date.now();
      const dt = last.current === null ? 0 : (now - last.current) / 1000;
      last.current = now;
      setT(prev => { const next = prev + dt * rate; if (next >= span) { setPlaying(false); return span; } return next; });
    }, 50);
    return () => clearInterval(id);
  }, [playing, rate, span]);
  const play = useCallback(() => { setT(prev => (prev >= span ? 0 : prev)); setPlaying(true); }, [span]);
  const pause = useCallback(() => setPlaying(false), []);
  const seek = useCallback((v: number) => setT(Math.min(span, Math.max(0, v))), [span]);
  return { t, ts: startTs + t, span, playing, rate, play, pause, seek, setRate, toggle: () => (playing ? pause() : play()) };
}
